import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card.tsx';
import { BarChart3, Scan, Calendar, TrendingUp } from 'lucide-react';
import { CreditChangeAnimation } from '../ui/CreditChangeAnimation.tsx';
import type { UserProfile } from '../../hooks/useUserProfile.ts';

interface UsageSectionProps {
  profile: UserProfile;
}

export const UsageSection: React.FC<UsageSectionProps> = ({ profile }) => {
  const [showAnimation, setShowAnimation] = useState(false);
  const [animationData, setAnimationData] = useState<{
    oldValue: number | 'unlimited';
    newValue: number | 'unlimited';
    type: 'credits' | 'scans';
  } | null>(null);
  const [previousUsage, setPreviousUsage] = useState(profile.usage);

  console.log('[UsageSection] Rendering with profile:', {
    isPro: profile.isPro,
    hasOneTimeScans: profile.hasOneTimeScans,
    usage: profile.usage,
    loading: profile.loading,
  });

  // Watch for changes in usage data to trigger animations
  useEffect(() => {
    if (profile.loading || !previousUsage) {
      setPreviousUsage(profile.usage);
      return;
    }

    const oldTotal = previousUsage.totalScansUsed;
    const newTotal = profile.usage.totalScansUsed;
    const oldAvailable = previousUsage.totalScansAvailable;
    const newAvailable = profile.usage.totalScansAvailable;

    // Trigger animation for scan usage changes
    if (oldTotal !== newTotal) {
      setAnimationData({
        oldValue: oldTotal,
        newValue: newTotal,
        type: 'scans'
      });
      setShowAnimation(true);
    }
    // Trigger animation for credit changes (when plan changes)
    else if (oldAvailable !== newAvailable) {
      setAnimationData({
        oldValue: typeof oldAvailable === 'number' ? oldAvailable : 0,
        newValue: newAvailable,
        type: 'credits'
      });
      setShowAnimation(true);
    }

    setPreviousUsage(profile.usage);
  }, [profile.usage, previousUsage, profile.loading]);

  if (profile.loading) {
    return (
      <Card className="p-6">
        <div className="h-6 bg-gray-200 rounded-md w-36 mb-4 animate-pulse"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded-md w-full animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded-md w-3/4 animate-pulse"></div>
          <div className="h-2 bg-gray-200 rounded-md w-full animate-pulse"></div>
        </div>
      </Card>
    );
  }

  const usage = profile.usage;
  const totalScans = usage.totalScansUsed;
  const availableScans = usage.totalScansAvailable;
  const isUnlimited = availableScans === 'unlimited' || availableScans === 9999;
  const oneTimeScansRemaining = profile.entitlements.reduce((sum, ent) => sum + ent.scans_remaining, 0);

  // Calculate usage percentage for progress bar
  const usagePercentage = !isUnlimited && typeof availableScans === 'number' && availableScans > 0
    ? Math.min((totalScans / availableScans) * 100, 100)
    : 0;

  const formatNextReset = (dateString?: string) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const now = new Date();
      const daysUntilReset = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysUntilReset <= 0) return 'Resets today';
      if (daysUntilReset === 1) return 'Resets tomorrow';
      if (daysUntilReset <= 7) return `Resets in ${daysUntilReset} days`;
      
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  };

  const nextResetText = usage.hasActiveSubscription 
    ? formatNextReset(usage.nextResetDate)
    : null;

  return (
    <>
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2 text-orange-400" />
          Scan Usage
        </h3>
        
        <div className="space-y-4">
          {/* Main usage display */}
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-400 flex items-center">
              <Scan className="h-4 w-4 mr-1" />
              Scans Used
            </span>
            <span className="text-sm font-semibold text-gray-200">
              {totalScans} {isUnlimited ? '(Unlimited)' : `/ ${availableScans}`}
            </span>
          </div>

          {/* Progress bar for non-unlimited plans */}
          {!isUnlimited && typeof availableScans === 'number' && availableScans > 0 && (
            <div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    usagePercentage >= 90 ? 'bg-red-500' :
                    usagePercentage >= 70 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${usagePercentage}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{totalScans} used</span>
                <span>{Math.max(0, (typeof availableScans === 'number' ? availableScans : 0) - totalScans)} remaining</span>
              </div>
            </div>
          )}

          {/* Plan type indicator */}
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-400">Plan Type</span>
            <span className="text-sm text-gray-200">
              {profile.isPro ? 'Pro Subscription' : 
               profile.hasOneTimeScans ? 'One-Time Scans' : 'Free Plan'}
            </span>
          </div>

          {/* One-time scans remaining */}
          {profile.hasOneTimeScans && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-400">One-Time Scans</span>
              <span className="text-sm font-medium text-blue-400">
                {oneTimeScansRemaining} remaining
              </span>
            </div>
          )}

          {/* Next reset date for Pro users */}
          {usage.hasActiveSubscription && nextResetText && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-400 flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Usage Resets
              </span>
              <span className="text-sm text-gray-200">{nextResetText}</span>
            </div>
          )}

          {/* Usage trend indicator */}
          {profile.isPro && typeof availableScans === 'number' && availableScans > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500 flex items-center">
                <TrendingUp className="h-4 w-4 mr-1" />
                Usage Level
              </span>
              <span className={`text-sm font-medium ${
                usagePercentage >= 90 ? 'text-red-600' :
                usagePercentage >= 70 ? 'text-yellow-600' :
                usagePercentage >= 50 ? 'text-blue-600' :
                'text-green-600'
              }`}>
                {usagePercentage >= 90 ? 'Very High' :
                 usagePercentage >= 70 ? 'High' :
                 usagePercentage >= 50 ? 'Moderate' :
                 'Low'}
              </span>
            </div>
          )}
        </div>

        {/* Status message */}
        <div className="mt-4">
          {!profile.isPro && !profile.hasOneTimeScans && (
            <div className="p-3 bg-gray-700/50 border border-gray-600 rounded-lg">
              <p className="text-sm text-gray-300">
                You currently have no scan credits. Upgrade to Pro or purchase one-time scans to continue.
              </p>
            </div>
          )}
          
          {!isUnlimited && usagePercentage >= 90 && (
            <div className="p-3 bg-red-900/30 border border-red-700/50 rounded-lg">
              <p className="text-sm text-red-300">
                You've used most of your scan allocation. Consider upgrading to Pro for unlimited scans.
              </p>
            </div>
          )}
          
          {profile.isPro && isUnlimited && (
            <div className="p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
              <p className="text-sm text-green-300">
                You have unlimited scans with your Pro subscription. Scan as much as you need!
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Credit Change Animation */}
      {showAnimation && animationData && (
        <CreditChangeAnimation
          oldValue={animationData.oldValue}
          newValue={animationData.newValue}
          type={animationData.type}
          show={showAnimation}
          onComplete={() => setShowAnimation(false)}
        />
      )}
    </>
  );
}; 